import {Component, OnDestroy, OnInit, HostListener} from '@angular/core';
import {UserUtilsService} from "../../lib/services/user/user-utils.service";
import {DomSanitizer} from "@angular/platform-browser";
import {TranslateService} from "@ngx-translate/core";
import {HttpClient} from "@angular/common/http";
import {StorageKeysEnum} from "../../lib/storage/storage-keys.enum";
import * as pubsub from 'pubsub-js';

interface Category {
    label: string;
    icon: string;
    enable: boolean;
    content?: CategoryContent[];
    jsonFile: string
}

interface CategoryContent {
    title: string;
    descriptionKey?: string; // Clave para la traducción
    description?: string; // Para compatibilidad con el código existente
    componentKey?: string; // Clave para el componente dinámico
    image?: string;
}

@Component({
    selector: 'app-discover-your-bank',
    templateUrl: './discover-your-bank.component.html',
    styleUrls: ['./discover-your-bank.component.scss'],
})
export class DiscoverYourBankComponent implements OnInit, OnDestroy {
    private readonly storageListener: (event: StorageEvent) => void;
    userInfo: any;
    fullName: string = "";
    lastLogOn!: formatDate;
    currentLang: string = "es";
    activeIndex: number | undefined = 0;
    categories: Category[] = [];
    selectedCategory: Category | null = null;
    jsonCategory: any;

    constructor(
        private sanitizer: DomSanitizer,
        private translate: TranslateService,
        private http: HttpClient
    ) {
        this.storageListener = (event: StorageEvent) => {
            if (event.key === StorageKeysEnum.ACCESS_TOKEN_WSO && !event.newValue) {
                window.close();
            }
        };
    }

    ngOnInit() {
        window.addEventListener('storage', this.storageListener);
        window.addEventListener('unload', () => {
            sessionStorage.removeItem('DISCOVER_BANK_TAB_OPEN');
        });
        this.currentLang = sessionStorage.getItem(StorageKeysEnum.CURRENT_LANGUAGE) ?? "es"
        this.categories = [
            {
                label: 'initial',
                icon: 'initial.png',
                enable: true,
                content: [
                    {
                        title: 'Pantalla de inicio',
                        componentKey: 'initial.homeScreen',
                    }
                ],
                jsonFile: "initial_screen.json"
            },
            {
                label: 'admon',
                icon: 'admon.png',
                enable: true,
                content: [
                    {
                        title: 'Administración de cuentas',
                        componentKey: 'admon.accountManagement',
                    }
                ],
                jsonFile: "administracion_cuenta.json"
            },
            {
                label: 'payments',
                icon: 'payments.png',
                enable: true,
                content: [
                    {
                        title: 'Realizar pagos',
                        componentKey: 'payments.makePayments',
                    }
                ],
                jsonFile: "payments.json"
            },
            {
                label: 'notifications',
                icon: 'notifications.png',
                enable: true,
                content: [
                    {
                        title: 'Centro de notificaciones',
                        componentKey: 'notifications.notificationCenter',
                    }
                ],
                jsonFile: "notificaciones.json"
            },
            {label: 'currencies', icon: 'currencies.png', enable: false, jsonFile: ""},
            {label: 'investments', icon: 'investments.png', enable: false, jsonFile: ""},
            {label: 'derivados', icon: 'derivados.png', enable: false, jsonFile: ""},
            {label: 'taxes', icon: 'taxes.png', enable: false, jsonFile: ""},
            {
                label: 'mancomunados',
                icon: 'mancomunados.png',
                enable: true,
                content: [
                    {
                        title: 'Facultades mancomunadas',
                        componentKey: 'mancomunados.jointPowers',
                    }
                ],
                jsonFile: "facultades_mancomunadas.json"
            },
            {label: 'cobranza', icon: 'cobranza.png', enable: false, jsonFile: ""},
            {label: 'hub', icon: 'hub.png', enable: false, jsonFile: ""},
            {label: 'credit', icon: 'credit.png', enable: false, jsonFile: ""},
            {label: 'trader', icon: 'trader.png', enable: false, jsonFile: ""},
            {label: 'c_dinamica', icon: 'c_dinamica.png', enable: false, jsonFile: ""},
            {
                label: 'configuration',
                icon: 'configuration.png',
                enable: true,
                content: [
                    {
                        title: 'Configuración general',
                        componentKey: 'configuration.generalConfig',
                    },
                    {
                        title: 'Seguridad',
                        componentKey: 'configuration.security',
                    }
                ],
                jsonFile: "configuration.json"
            },
        ]
        pubsub.subscribe("changeLanguage", async (msg: any, data: any) => {
            this.currentLang = data;
            this.getJsonFile(this.selectedCategory!);
        })
    }

    activeIndexChange(index: number) {
        this.activeIndex = index;
    }

    selectCategory(category: Category) {
        if (!category.enable) {
            return;
        }
        if (this.selectedCategory === category) {
            this.selectedCategory = null;
        } else {
            this.selectedCategory = category;
            this.activeIndex = 0;
            setTimeout(() => {
                const accordionElement = document.querySelector('.accordion-container');
                if (accordionElement) {
                    accordionElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 100);
        }
        this.getJsonFile(category);
    }

    isCategorySelected(category: Category): boolean {
        return this.selectedCategory === category;
    }


    ngOnDestroy() {
        window.removeEventListener('storage', this.storageListener);
        window.removeEventListener('unload', this.handleWindowClose);
        sessionStorage.removeItem('DISCOVER_BANK_TAB_OPEN');
    }

    @HostListener('window:beforeunload', ['$event'])
    handleWindowClose(event: Event) {
        sessionStorage.removeItem('DISCOVER_BANK_TAB_OPEN');
    }

    getIconLabel(category: any) {
        return `discoverYourBank.categories.${category.label}`
    }

    safeHtml(content: CategoryContent) {
        if (content.descriptionKey) {
            // Si existe una clave de traducción, obtener la traducción y sanitizarla
            const translatedText = this.translate.instant(content.descriptionKey);
            return this.sanitizer.bypassSecurityTrustHtml(translatedText);
        } else if (content.description) {
            // Para compatibilidad con el código existente
            return this.sanitizer.bypassSecurityTrustHtml(content.description);
        }
        return '';
    }


    getJsonFile(category: Category) {
        this.http.get(`assets/discoverYourBank/${category.jsonFile}`).subscribe({
            next: (data: any) => {
                this.jsonCategory = data[this.currentLang];
            },
            error: (err: any) => {
                this.jsonCategory = {}
            }
        })
    }

    getInnerHtml(feature: any) {
        const html = feature?.description;
        let htmlList = '';
        if (feature.subItems && feature.subItems?.length > 0) {
            const list = feature.subItems.map((item: any) => {
                return `<li>${item}</li>`
            }).join('')
            htmlList = `<ul>${list}</ul>`
        }
        return html + htmlList;
    }
}

type formatDate = {
    date: string,
    hour: string
}
